import { ThemeToggle } from '@/components/theme/ThemeToggle';
import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState, useEffect } from 'react';
import { Menu, Code, FolderPlus, Upload } from 'lucide-react';
import hostvslIcon from '@/assets/hostvsl-icon.webp';
import { Button } from '@/components/ui/button';
import { NotificationBell } from './NotificationBell';
import { cn } from '@/lib/utils';
import { useNavigate, useLocation } from 'react-router-dom';
import { CopyCodeModal } from '@/components/shared/CopyCodeModal';
import { UploadQueueModal } from '@/components/upload/UploadQueueModal';
import { TourChecklist } from '@/components/tour/TourChecklist';

interface MobileHeaderProps {
  onDrawerToggle: () => void;
  hasUnsavedChanges?: boolean;
  onSave?: () => void;
  isSaving?: boolean;
  isSaved?: boolean;
  videoId?: string;
  videoName?: string;
  userId?: string;
  onCreateFolder?: () => void;
  haveHeadline?: boolean;
  isHTMLSelected?: boolean;
  headlineHtmlCode?: string;
}

export function MobileHeader({
  onDrawerToggle,
  hasUnsavedChanges = false,
  onSave,
  isSaving = false,
  isSaved = false,
  videoId,
  videoName,
  userId,
  onCreateFolder,
  haveHeadline,
  isHTMLSelected,
  headlineHtmlCode,
}: MobileHeaderProps) {
  useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [showCopyCodeModal, setShowCopyCodeModal] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);

  const isDashboard = location.pathname === '/' || location.pathname === '/dashboard';

  // Detectar scroll do main para aplicar sombra dinâmica
  useEffect(() => {
    const mainElement = document.querySelector('main');
    
    const handleScroll = () => {
      if (mainElement) {
        setIsScrolled(mainElement.scrollTop > 10);
      }
    };
    
    mainElement?.addEventListener('scroll', handleScroll, { passive: true });
    return () => mainElement?.removeEventListener('scroll', handleScroll);
  }, []);
  
  const handleMenuClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onDrawerToggle();
  };
  
  return (
    <header
      className="mobile-header fixed top-0 left-0 right-0 z-[9999] flex h-[64px] items-center justify-between px-4 bg-white dark:bg-card lg:hidden shadow-[0_1px_4px_rgba(0,0,0,0.06)] dark:shadow-[0_1px_4px_rgba(0,0,0,0.3)]"
    >
      <div className="flex items-center gap-2 flex-1 min-w-0">
        <Button
          variant="ghost"
          size="icon"
          onClick={handleMenuClick}
          className="min-w-[44px] min-h-[44px] pl-0 flex-shrink-0"
          aria-label={translateInterfaceText("Abrir menu")}
        >
          <Menu className="h-5 w-5" />
        </Button>
        
        {/* Divisor Vertical Premium */}
        <div 
          className="h-[22px] w-[1px] flex-shrink-0 dark:bg-white/15"
          style={{ background: 'rgba(0,0,0,0.12)' }}
        />
        
        <div className="flex items-center min-w-0 flex-1">
          {/* Ícone dashboard: somente abaixo de 500px */}
          {isDashboard && (
            <img
              src="/assets/imgs/og-image.png"
              alt={translateInterfaceText("HostVSL")}
              className="h-11 w-11 object-cover rounded-lg flex-shrink-0 cursor-pointer transition-opacity hover:opacity-80 [@media(min-width:500px)]:hidden"
              onClick={() => { if (location.pathname !== '/dashboard') navigate('/dashboard'); }}
            />
          )}
          {/* Logo padrão: sempre para não-dashboard, e a partir de 500px para dashboard */}
          <img
            src="/assets/imgs/logo-azul-e-preta.png"
            alt={translateInterfaceText("HostVSL")}
            className={`h-8 flex-shrink-0 cursor-pointer transition-opacity hover:opacity-80${isDashboard ? ' hidden [@media(min-width:500px)]:block' : ''}`}
            onClick={() => { if (location.pathname !== '/dashboard') navigate('/dashboard'); }}
          />
        </div>
      </div>
      
      <div className="flex items-center gap-2 flex-shrink-0">
        <ThemeToggle compact />
        {!location.pathname.startsWith('/personalizations') && <TourChecklist />}
        {/* Badge de alterações não salvas - Mobile */}
        {hasUnsavedChanges && !isSaving && (
          <div className="flex items-center gap-1.5 px-2 h-[32px] rounded-lg">
            <div className="w-1.5 h-1.5 bg-orange-400 rounded-full animate-pulse" />
            <span className="text-[10px] font-medium text-gray-400 whitespace-nowrap">{translateInterfaceText("Não salvo")}</span>
          </div>
        )}

        {/* Botão Salvar - Mobile */}
        {hasUnsavedChanges && onSave && (
          <Button
            onClick={onSave}
            disabled={isSaving}
            size="sm"
            className="h-[32px] px-3 rounded-lg font-semibold text-xs shadow-md transition-all duration-200 border-0"
            style={{
              background: 'linear-gradient(135deg, #2D6CF6 0%, #1E5FE6 100%)',
              color: '#FFFFFF',
              boxShadow: '0 2px 8px rgba(45, 108, 246, 0.3)'
            }}
          >
            {isSaving ? (
              <>
                <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
              </>
            ) : (
              <>
                <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </>
            )}
          </Button>
        )}

        {/* Botão Copiar Código - Mobile - Aparece após salvar */}
        {isSaved && !hasUnsavedChanges && videoId && (
          <Button
            onClick={() => setShowCopyCodeModal(true)}
            size="sm"
            className="h-[32px] px-3 rounded-lg font-semibold text-xs shadow-md transition-all duration-200 border-0"
            style={{
              background: 'linear-gradient(135deg, #2D6CF6 0%, #1E5FE6 100%)',
              color: '#FFFFFF',
              boxShadow: '0 2px 8px rgba(45, 108, 246, 0.3)'
            }}
          >
            <Code className="h-3.5 w-3.5" />
          </Button>
        )}

        {/* <NotificationBell badge={3} /> */}

        {/* Botões de ação do dashboard */}
        {onCreateFolder && (
          <>
            <Button
              size="icon"
              onClick={onCreateFolder}
              className="h-[38px] w-[38px] rounded-[10px] flex-shrink-0 border"
              aria-label={translateInterfaceText("Nova pasta")}
              style={{ background: 'transparent', color: '#64748b', borderColor: '#D1D5DB' }}
            >
              <FolderPlus className="h-[18px] w-[18px]" />
            </Button>
            <Button
              size="icon"
              onClick={() => setUploadModalOpen(true)}
              className="h-[38px] w-[38px] rounded-[10px] flex-shrink-0 border-0"
              aria-label={translateInterfaceText("Adicionar vídeos")}
              style={{ background: '#296cff', color: '#FFFFFF', boxShadow: '0 2px 8px rgba(41, 108, 255, 0.3)' }}
            >
              <Upload className="h-[18px] w-[18px]" />
            </Button>
          </>
        )}
      </div>

      <UploadQueueModal open={uploadModalOpen} onOpenChange={setUploadModalOpen} />

      {/* Modal de Copiar Código */}
      {videoId && (
        <CopyCodeModal
          isOpen={showCopyCodeModal}
          onClose={() => setShowCopyCodeModal(false)}
          entityType="video"
          entityId={videoId}
          entityName={videoName || 'Vídeo'}
          userId={userId}
          haveHeadline={haveHeadline}
          isHTMLSelected={isHTMLSelected}
          headlineHtmlCode={headlineHtmlCode}
          defaultTab="js"
          hideJsTab={false}
        />
      )}
    </header>
  );
}
